

import { useState, useEffect } from "react"
import { MathText } from "./mathRenderer"
import AIResponse from "../AiResponse"
import Modal from "./SummaryModal"

function StudyPage({ summaryData, onClose }) {
  const [conceptCards, setConceptCards] = useState([])
  const [loadingConcepts, setLoadingConcepts] = useState(true)
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [aiResponse, setAiResponse] = useState("")
  const [loadingAI, setLoadingAI] = useState(false)
  const [activeTab, setActiveTab] = useState("questions")

  const [showConceptExplanation, setShowConceptExplanation] = useState(false)
  const [selectedConcept, setSelectedConcept] = useState(null)
  const [conceptExplanation, setConceptExplanation] = useState("")
  const [loadingConceptExplanation, setLoadingConceptExplanation] = useState(false)

  // Generate concept cards from feedback
  useEffect(() => {
    const generateConceptCards = async () => {
      if (!summaryData || !summaryData.questions) return

      setLoadingConcepts(true)
      try {
        // Collect all feedback from questions
        const allFeedback = summaryData.questions.map((q) => q.feedback).join("\n\n")

        const response = await fetch("/api/exam-question-analysis", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: "concept_extraction",
            content: allFeedback,
            examTitle: summaryData.folderName,
          }),
        })

        const data = await response.json()

        if (data.success && data.concepts) {
          setConceptCards(data.concepts)
        } else {
          // Fallback: create basic concept cards from feedback
          const concepts = extractBasicConcepts(allFeedback)
          setConceptCards(concepts)
        }
      } catch (error) {
        console.error("Error generating concept cards:", error)
        // Fallback: create basic concept cards from feedback
        const allFeedback = summaryData.questions.map((q) => q.feedback).join("\n\n")
        const concepts = extractBasicConcepts(allFeedback)
        setConceptCards(concepts)
      } finally {
        setLoadingConcepts(false)
      }
    }

    generateConceptCards()
  }, [summaryData])

  // Fallback function to extract basic concepts
  const extractBasicConcepts = (feedback) => {
    const concepts = []
    const commonMathTopics = [
      "Algebra",
      "Calculus",
      "Geometry",
      "Trigonometry",
      "Statistics",
      "Probability",
      "Linear Algebra",
      "Differential Equations",
      "Functions",
    ]

    commonMathTopics.forEach((topic, index) => {
      if (feedback.toLowerCase().includes(topic.toLowerCase())) {
        concepts.push({
          id: index + 1,
          title: topic,
          description: `Key concepts and principles related to ${topic} based on your exam feedback.`,
          difficulty: Math.random() > 0.5 ? "Medium" : "Easy",
          needsAttention: Math.random() > 0.6, // Random for fallback
          relatedQuestions: [],
        })
      }
    })

    // Add at least 3 concepts if none found
    if (concepts.length === 0) {
      concepts.push(
        {
          id: 1,
          title: "Problem Solving",
          description: "General problem-solving strategies and approaches for mathematical problems.",
          difficulty: "Medium",
          needsAttention: true,
          relatedQuestions: [],
        },
        {
          id: 2,
          title: "Mathematical Reasoning",
          description: "Logical thinking and reasoning skills for mathematical proofs and solutions.",
          difficulty: "Medium",
          needsAttention: true,
          relatedQuestions: [],
        },
        {
          id: 3,
          title: "Calculation Accuracy",
          description: "Techniques for improving computational accuracy and avoiding common errors.",
          difficulty: "Easy",
          needsAttention: true,
          relatedQuestions: [],
        },
      )
    }

    return concepts
  }

  // Handle AI analysis requests
  const handleAIRequest = async (question, type) => {
    setSelectedQuestion(question)
    setLoadingAI(true)
    setAiResponse("")

    try {
      const requestBody = {
        questionContent: question.questionContent,
        studentSolution: question.studentSolution,
        feedback: question.feedback,
        type: type,
      }

      const response = await fetch("/api/exam-question-analysis", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })

      const data = await response.json()

      if (data.success) {
        setAiResponse(data.response)
      } else {
        setAiResponse("Sorry, I couldn't generate a response at this time. Please try again later.")
      }
    } catch (error) {
      console.error("Error getting AI response:", error)
      setAiResponse("An error occurred while processing your request. Please try again.")
    } finally {
      setLoadingAI(false)
    }
  }

  // Handle concept explanation
  const handleConceptExplanation = async (concept) => {
    setSelectedConcept(concept)
    setLoadingConceptExplanation(true)
    setShowConceptExplanation(true)
    setConceptExplanation("")

    try {
      const response = await fetch("/api/exam-question-analysis", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "teach_concept",
          questionContent: concept.title,
          studentSolution: concept.description,
        }),
      })

      const data = await response.json()

      if (data.success) {
        setConceptExplanation(data.response)
      } else {
        setConceptExplanation("Sorry, I couldn't generate an explanation at this time. Please try again later.")
      }
    } catch (error) {
      console.error("Error getting concept explanation:", error)
      setConceptExplanation("An error occurred while processing your request. Please try again.")
    } finally {
      setLoadingConceptExplanation(false)
    }
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "easy":
        return "bg-green-100 text-green-800"
      case "medium":
        return "bg-yellow-100 text-yellow-800"
      case "hard":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return "text-green-600"
    if (percentage >= 60) return "text-yellow-600"
    return "text-red-600"
  }

  const getRelatedConcepts = (questionNumber) => {
    return conceptCards.filter(
      (concept) => concept.relatedQuestions && concept.relatedQuestions.includes(questionNumber),
    )
  }

  return (
    <Modal isOpen={true} onClose={onClose}>
      <div className="absolute bg-white rounded-lg shadow-xl w-[95vw] h-[95vh] left-1/2 transform -translate-x-1/2 top-1/2 transform -translate-y-1/2 flex flex-col">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Study for the Exam</h2>
            <p className="text-gray-600 mt-1">{summaryData?.folderName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 pt-4">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setActiveTab("concepts")}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === "concepts" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
                }`}
            >
              Key Concepts ({conceptCards.length})
            </button>
            <button
              onClick={() => setActiveTab("questions")}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === "questions" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
                }`}
            >
              Question Analysis ({summaryData?.questions?.length || 0})
            </button>
          </div>
        </div>

        {/* Concept Explanation View */}
        {showConceptExplanation && (
          <div className="flex-1 overflow-hidden">
            <div className="p-6 h-full overflow-y-auto">
              <div className="mb-4 flex items-center">
                <button
                  onClick={() => {
                    setShowConceptExplanation(false)
                    setSelectedConcept(null)
                    setConceptExplanation("")
                  }}
                  className="flex items-center text-blue-600 hover:text-blue-800 font-medium mr-4"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Concepts
                </button>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">{selectedConcept?.title}</h3>
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getDifficultyColor(selectedConcept?.difficulty)}`}
                  >
                    {selectedConcept?.difficulty}
                  </span>
                </div>
              </div>

              {loadingConceptExplanation ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Generating detailed explanation...</p>
                  </div>
                </div>
              ) : conceptExplanation ? (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <AIResponse content={conceptExplanation} useTypewriter={false} />
                </div>
              ) : (
                <div className="text-center text-gray-500 mt-8">
                  <p>Select a concept to see detailed explanation</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        {!showConceptExplanation && (
          <div className="flex-1 overflow-hidden">
            {activeTab === "concepts" && (
              <div className="p-6 h-full overflow-y-auto">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Key Concepts from Your Exam</h3>
                  <p className="text-gray-600 text-sm">
                    These concepts were identified from the feedback on your exam questions.
                  </p>
                </div>

                {loadingConcepts ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                      <p className="text-gray-600">Analyzing your exam feedback...</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* High Priority Concepts */}
                    {conceptCards.filter((concept) => concept.needsAttention).length > 0 && (
                      <div>
                        <div className="flex items-center mb-4">
                          <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                          <h4 className="text-lg font-semibold text-gray-800">Needs Immediate Attention</h4>
                          <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                            {conceptCards.filter((concept) => concept.needsAttention).length}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                          {conceptCards
                            .filter((concept) => concept.needsAttention)
                            .map((concept) => (
                              <div
                                key={concept.id}
                                className="bg-white border-l-4 border-l-red-500 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                              >
                                <div className="flex justify-between items-start mb-3">
                                  <h5 className="font-semibold text-gray-800">{concept.title}</h5>
                                  <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(concept.difficulty)}`}
                                  >
                                    {concept.difficulty}
                                  </span>
                                </div>
                                <p className="text-gray-600 text-sm mb-3">{concept.description}</p>
                                {concept.relatedQuestions && concept.relatedQuestions.length > 0 && (
                                  <div className="mb-3">
                                    <span className="text-xs font-medium text-gray-500">Related Questions: </span>
                                    <span className="text-xs text-blue-600">{concept.relatedQuestions.join(", ")}</span>
                                  </div>
                                )}
                                <button
                                  onClick={() => handleConceptExplanation(concept)}
                                  className="w-full bg-red-500 text-white py-2 px-4 rounded-md hover:bg-red-600 transition-colors text-sm font-medium"
                                >
                                  Learn More
                                </button>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Regular Priority Concepts */}
                    {conceptCards.filter((concept) => !concept.needsAttention).length > 0 && (
                      <div>
                        <div className="flex items-center mb-4">
                          <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                          <h4 className="text-lg font-semibold text-gray-800">Good to Review</h4>
                          <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                            {conceptCards.filter((concept) => !concept.needsAttention).length}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {conceptCards
                            .filter((concept) => !concept.needsAttention)
                            .map((concept) => (
                              <div
                                key={concept.id}
                                className="bg-white border-l-4 border-l-blue-500 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                              >
                                <div className="flex justify-between items-start mb-3">
                                  <h5 className="font-semibold text-gray-800">{concept.title}</h5>
                                  <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(concept.difficulty)}`}
                                  >
                                    {concept.difficulty}
                                  </span>
                                </div>
                                <p className="text-gray-600 text-sm mb-3">{concept.description}</p>
                                {concept.relatedQuestions && concept.relatedQuestions.length > 0 && (
                                  <div className="mb-3">
                                    <span className="text-xs font-medium text-gray-500">Related Questions: </span>
                                    <span className="text-xs text-blue-600">{concept.relatedQuestions.join(", ")}</span>
                                  </div>
                                )}
                                <button
                                  onClick={() => handleConceptExplanation(concept)}
                                  className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition-colors text-sm font-medium"
                                >
                                  Learn More
                                </button>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "questions" && (
              <div className="flex h-full">
                {/* Questions List */}
                <div className="w-1/2 p-6 border-r border-gray-200 overflow-y-auto">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Your Exam Questions</h3>
                    <p className="text-gray-600 text-sm">Click on any question to analyze it with AI assistance.</p>
                  </div>

                  <div className="space-y-4">
                    {summaryData?.questions?.map((question, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedQuestion === question
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                          }`}
                        onClick={() => setSelectedQuestion(question)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-800">
                              Question {question.questionNumber || index + 1}
                            </h4>
                            {/* Related Concepts */}
                            {getRelatedConcepts(question.questionNumber || index + 1).length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {getRelatedConcepts(question.questionNumber || index + 1).map((concept) => (
                                  <button
                                    key={concept.id}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleConceptExplanation(concept)
                                    }}
                                    className={`text-xs px-2 py-1 rounded-full font-medium transition-colors ${concept.needsAttention
                                      ? "bg-red-100 text-red-700 hover:bg-red-200"
                                      : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                      }`}
                                  >
                                    {concept.title}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className={`text-sm font-semibold ${getScoreColor(question.scorePercentage)}`}>
                            {question.score} ({question.scorePercentage}%)
                          </span>
                        </div>

                        <div className="text-sm text-gray-600 mb-3">
                          <div className="mb-2">
                            <span className="font-medium">Question:</span>
                            <div className="mt-1">
                              <MathText text={question.questionContent.substring(0, 100) + "..."} />
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Your Answer:</span>
                            <div className="mt-1">
                              <MathText text={question.studentSolution.substring(0, 100) + "..."} />
                            </div>
                          </div>
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleAIRequest(question, "question_analysis")
                            }}
                            className="flex-1 bg-purple-500 text-white py-1 px-3 rounded text-xs font-medium hover:bg-purple-600 transition-colors"
                          >
                            Question Analysis
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleAIRequest(question, "teach_me")
                            }}
                            className="flex-1 bg-green-500 text-white py-1 px-3 rounded text-xs font-medium hover:bg-green-600 transition-colors"
                          >
                            Teach Me
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleAIRequest(question, "similar_questions")
                            }}
                            className="flex-1 bg-orange-500 text-white py-1 px-3 rounded text-xs font-medium hover:bg-orange-600 transition-colors"
                          >
                            Similar Questions
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Response Panel */}
                <div className="w-1/2 p-6 overflow-y-auto bg-gray-50">
                  {selectedQuestion ? (
                    <div>
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                          AI Analysis - Question {selectedQuestion.questionNumber}
                        </h3>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
                          <div className="mb-3">
                            <span className="font-medium text-gray-700">Question:</span>
                            <div className="mt-1">
                              <MathText text={selectedQuestion.questionContent} />
                            </div>
                          </div>
                          <div className="mb-3">
                            <span className="font-medium text-gray-700">Your Solution:</span>
                            <div className="mt-1">
                              <MathText text={selectedQuestion.studentSolution} />
                            </div>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Score:</span>
                            <span className={`ml-2 font-semibold ${getScoreColor(selectedQuestion.scorePercentage)}`}>
                              {selectedQuestion.score} ({selectedQuestion.scorePercentage}%)
                            </span>
                          </div>
                        </div>
                      </div>

                      {loadingAI ? (
                        <div className="flex items-center justify-center h-64">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                            <p className="text-gray-600">AI is analyzing your question...</p>
                          </div>
                        </div>
                      ) : aiResponse ? (
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                          <AIResponse content={aiResponse} useTypewriter={false} />
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 mt-8">
                          <svg
                            className="w-16 h-16 mx-auto mb-4 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                            />
                          </svg>
                          <p>Select an analysis option above to get AI assistance</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 mt-8">
                      <svg
                        className="w-16 h-16 mx-auto mb-4 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                      <p>Select a question from the list to analyze</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default StudyPage
